import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { clsx } from 'clsx';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showItemsPerPage?: boolean;
  showCurrentRange?: boolean;
  className?: string;
  disabled?: boolean;
  maxVisiblePages?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  showCurrentRange = true,
  className,
  disabled = false,
  maxVisiblePages = 7
}) => {
  // Calculate current range
  const startItem = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display with improved logic
  const generatePageNumbers = (): (number | string)[] => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const sidePages = Math.floor((maxVisiblePages - 3) / 2); // Reserve space for first, last, and ellipsis

    // Always show first page
    pages.push(1);

    // Calculate the range around current page
    let startPage = Math.max(2, currentPage - sidePages);
    let endPage = Math.min(totalPages - 1, currentPage + sidePages);

    // Adjust range if we're near the beginning
    if (currentPage <= sidePages + 2) {
      endPage = Math.min(totalPages - 1, maxVisiblePages - 2);
      startPage = 2;
    }

    // Adjust range if we're near the end
    if (currentPage >= totalPages - sidePages - 1) {
      startPage = Math.max(2, totalPages - maxVisiblePages + 2);
      endPage = totalPages - 1;
    }

    // Add ellipsis after first page if there's a gap
    if (startPage > 2) {
      pages.push('...');
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if there's a gap
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !disabled) {
      onPageChange(page);
    }
  };

  const itemsPerPageOptions = [5, 10, 20, 50, 100];

  return (
    <div className={clsx(
      'flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-lg',
      className
    )}>
      {/* Left side - Count information */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 dark:text-gray-400">
        {showCurrentRange && totalItems > 0 && (
          <div className="flex items-center gap-1">
            <span>Showing</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {startItem.toLocaleString()}-{endItem.toLocaleString()}
            </span>
            <span>of {totalItems.toLocaleString()}</span>
          </div>
        )}

        {showItemsPerPage && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              disabled={disabled}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span>per page</span>
          </div>
        )}
      </div>

      {/* Right side - Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* First page button */}
          <motion.button
            whileHover={{ scale: disabled || currentPage === 1 ? 1 : 1.05 }}
            whileTap={{ scale: disabled || currentPage === 1 ? 1 : 0.95 }}
            onClick={() => handlePageChange(1)}
            disabled={disabled || currentPage === 1}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              disabled || currentPage === 1
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </motion.button>

          {/* Previous page button */}
          <motion.button
            whileHover={{ scale: disabled || currentPage === 1 ? 1 : 1.05 }}
            whileTap={{ scale: disabled || currentPage === 1 ? 1 : 0.95 }}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || currentPage === 1}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              disabled || currentPage === 1
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </motion.button>

          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-2">
            {pageNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-gray-400 dark:text-gray-600">...</span>
                ) : (
                  <motion.button
                    whileHover={{ scale: disabled ? 1 : 1.05 }}
                    whileTap={{ scale: disabled ? 1 : 0.95 }}
                    onClick={() => handlePageChange(page as number)}
                    disabled={disabled}
                    className={clsx(
                      'min-w-[2.5rem] px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      currentPage === page
                        ? 'bg-blue-600 text-white shadow-sm'
                        : disabled
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    {page}
                  </motion.button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next page button */}
          <motion.button
            whileHover={{ scale: disabled || currentPage === totalPages ? 1 : 1.05 }}
            whileTap={{ scale: disabled || currentPage === totalPages ? 1 : 0.95 }}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || currentPage === totalPages}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              disabled || currentPage === totalPages
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </motion.button>

          {/* Last page button */}
          <motion.button
            whileHover={{ scale: disabled || currentPage === totalPages ? 1 : 1.05 }}
            whileTap={{ scale: disabled || currentPage === totalPages ? 1 : 0.95 }}
            onClick={() => handlePageChange(totalPages)}
            disabled={disabled || currentPage === totalPages}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              disabled || currentPage === totalPages
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
            title="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
